import { DragEndEvent } from '@dnd-kit/core';
import { arrayMove } from '@dnd-kit/sortable';
import { difference } from 'lodash-es';
import { useState } from 'react';

// data - set of default items to be draggable
// syncOnInitial - if true, will sync the initial items with the provided data
export const useDraggable = <T, K extends keyof T>(data: T[], dataKey: K) => {
	const [originItems, setOriginItems] = useState(
		data.map((item) => item[dataKey])
	);

	const dataMap = data.reduce((acc, e) => {
		acc.set(e[dataKey] as K, e);

		return acc;
	}, new Map<K, T>());

	const onDragEnd = ({ active, over }: DragEndEvent) => {
		if (!active || !over) {
			return originItems;
		}

		let newItems = originItems;
		if (active.id !== over.id) {
			setOriginItems((items) => {
				const oldIndex = items.indexOf(active.id.toString() as T[K]);
				const newIndex = items.indexOf(over.id.toString() as T[K]);
				newItems = arrayMove(items, oldIndex, newIndex);

				return newItems;
			});
		}

		return newItems;
	};

	// onInitial is used to set the initial items when the component mounts
	// initialItems - the items to add to the origin data
	// preferOriginItems - if true, will add the initial items, which are defined in the origin data
	// syncOnInitial - if true, will sync the initial items with the existing originItems
	const onInitial = (
		initialItems?: T[K][],
		preferOriginItems = true,
		syncOnInitial = true
	) => {
		if (!initialItems || initialItems.length === 0) {
			return;
		}

		let items = initialItems;

		if (preferOriginItems) {
			items = initialItems.filter((item) => originItems.includes(item));
		}

		if (syncOnInitial) {
			items = [...items, ...difference(originItems, initialItems)];
		}

		setOriginItems(items);
	};

	return { dataMap, items: originItems, onDragEnd, onInitial };
};
